/**
 * useSwipeNavigation Hook
 *
 * Provides swipe gesture detection for mobile navigation between adjacent locations.
 * Supports horizontal and vertical swipes with configurable thresholds.
 */

import { useEffect, useRef, useCallback } from 'react';

export type SwipeDirection = 'left' | 'right' | 'up' | 'down';

export interface SwipeNavigationOptions {
  /** Callback when user swipes left */
  onSwipeLeft?: () => void;
  /** Callback when user swipes right */
  onSwipeRight?: () => void;
  /** Callback when user swipes up */
  onSwipeUp?: () => void;
  /** Callback when user swipes down */
  onSwipeDown?: () => void;
  /** Generic swipe callback with direction */
  onSwipe?: (direction: SwipeDirection) => void;
  /** Minimum distance in pixels to trigger a swipe (default: 50) */
  threshold?: number;
  /** Maximum time in ms for a swipe gesture (default: 300) */
  maxTime?: number;
  /** Whether swipe navigation is enabled (default: true) */
  enabled?: boolean;
  /** Target element ref (uses document if not provided) */
  targetRef?: React.RefObject<HTMLElement>;
}

interface TouchState {
  startX: number;
  startY: number;
  startTime: number;
  isTracking: boolean;
}

/** Default minimum distance in pixels to trigger a swipe */
export const DEFAULT_THRESHOLD = 50;
/** Default maximum time in ms for a swipe gesture */
export const DEFAULT_MAX_TIME = 300;

/**
 * Hook for detecting swipe gestures
 */
export function useSwipeNavigation(options: SwipeNavigationOptions = {}): void {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    onSwipe,
    threshold = DEFAULT_THRESHOLD,
    maxTime = DEFAULT_MAX_TIME,
    enabled = true,
    targetRef,
  } = options;

  const touchState = useRef<TouchState>({
    startX: 0,
    startY: 0,
    startTime: 0,
    isTracking: false,
  });

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enabled) return;

      const touch = e.touches[0];
      touchState.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        isTracking: true,
      };
    },
    [enabled]
  );

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!enabled || !touchState.current.isTracking) return;

      const touch = e.changedTouches[0];
      const endX = touch.clientX;
      const endY = touch.clientY;
      const endTime = Date.now();

      const { startX, startY, startTime } = touchState.current;
      touchState.current.isTracking = false;

      // Check if gesture was too slow
      const timeDiff = endTime - startTime;
      if (timeDiff > maxTime) return;

      // Calculate distances
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      // Determine if this is a valid swipe
      if (absDeltaX < threshold && absDeltaY < threshold) return;

      // Determine swipe direction
      let direction: SwipeDirection;

      if (absDeltaX > absDeltaY) {
        // Horizontal swipe
        direction = deltaX > 0 ? 'right' : 'left';
      } else {
        // Vertical swipe
        direction = deltaY > 0 ? 'down' : 'up';
      }

      // Call appropriate callback
      if (onSwipe) {
        onSwipe(direction);
      }

      switch (direction) {
        case 'left':
          onSwipeLeft?.();
          break;
        case 'right':
          onSwipeRight?.();
          break;
        case 'up':
          onSwipeUp?.();
          break;
        case 'down':
          onSwipeDown?.();
          break;
      }
    },
    [enabled, threshold, maxTime, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, onSwipe]
  );

  const handleTouchCancel = useCallback(() => {
    touchState.current.isTracking = false;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const target = targetRef?.current || document;

    target.addEventListener('touchstart', handleTouchStart as EventListener, {
      passive: true,
    });
    target.addEventListener('touchend', handleTouchEnd as EventListener, {
      passive: true,
    });
    target.addEventListener('touchcancel', handleTouchCancel as EventListener, {
      passive: true,
    });

    return () => {
      target.removeEventListener('touchstart', handleTouchStart as EventListener);
      target.removeEventListener('touchend', handleTouchEnd as EventListener);
      target.removeEventListener('touchcancel', handleTouchCancel as EventListener);
    };
  }, [enabled, targetRef, handleTouchStart, handleTouchEnd, handleTouchCancel]);
}

export default useSwipeNavigation;
